import { useState, useCallback } from "react";
import { RehabFacilityDto, rehabFacilityFormData, rehabFacIdKey } from "../types/rehabFacility";
import { rehabFacilityService } from "../services/rehabFacilityService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useRehabFacilities = () => {
  // 1. Το GET γίνεται αυτόματα με το useApi, cache και signal
  const {
    data: rehabFacilities = [],
    loading: isFetching,
    error: getError,
    refetch: fetchRehabFacilities,
    invalidateCache,
  } = useApi<RehabFacilityDto[]>("rehab-facilities", (signal) => rehabFacilityService.getAll({}, signal));

  // 2. Το CREATE τυλίγεται στο καθαρό useMutation
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newFacility:typeof rehabFacilityFormData) => rehabFacilityService.create(newFacility));

  // 3. Το UPDATE τυλίγεται στο καθαρό useMutation
  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: RehabFacilityDto }) =>
    rehabFacilityService.update(id, data)
  );

  // 4. Το DELETE τυλίγεται στο καθαρό useMutation
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => rehabFacilityService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createRehabFacility = async (newFacility:typeof rehabFacilityFormData): Promise<void> => {
    try {
      await createExecution(newFacility);
      
      // Καθαρισμός cache για να φέρει τα νέα δεδομένα στην επόμενη προβολή
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το κέντρο αποκατάστασης αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      
      fetchRehabFacilities(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updateRehabFacility = async (id: number | string, updatedData: RehabFacilityDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRehabFacilities(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteRehabFacilities = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      
      // Καθαρισμός cache
      invalidateCache();
      
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      
      fetchRehabFacilities(true); // Άμεση ανανέωση λίστας
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    rehabFacilities,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError,
    fetchRehabFacilities: () => fetchRehabFacilities(true),
    createRehabFacility,
    updateRehabFacility,
    deleteRehabFacilities,
    modalState,
    closeModal,
  };
};